import type { PmoAreaResult, PmoReportModel } from "./pmoReport";
import { shareOfScale } from "./share.ts";

// The assessment dashboard (DESIGN_PROPOSAL_DASHBOARD 271026 V1.1, screen 5),
// first slice: one submitted PMO self-assessment, as numbers and shapes.
//
// The dashboard derives NOTHING of its own. Every value here is a value the
// report model already carries (pmoReport.ts, template 1.2.x): the overall,
// its coverage and partial marker, the per-area averages and statuses, the
// focus areas and the recommendations. This module only arranges them for a
// screen that leads with shapes, so the dashboard and the PDF report can
// never disagree (proposal §3, last paragraph). It names no person (§6).

export type Tone = "ok" | "warn" | "muted" | "neutral";

export interface DashboardKpi {
  key: "overall" | "coverage" | "areas" | "recommendations";
  label: string;
  value: string;
  detail: string | null;
  tone: Tone;
}

export interface AreaBar {
  blockId: string;
  sequence: number;
  title: string;
  status: PmoAreaResult["status"];
  /** the average's share of the answer scale (lib/share.ts), 0..1 — only for a complete area */
  ratio: number | null;
  average: number | null;
  state: string | null;
  /** 1..3 when the area is a suggested focus area */
  focusRank: number | null;
  assessed: number;
  applicable: number;
  notInPlace: number;
  /** the respondent's own reason, when the whole area is not applicable */
  justification: string | null;
}

export interface AttentionItem {
  kind: "incomplete" | "not_applicable";
  blockId: string;
  title: string;
  text: string;
}

export interface FocusItem {
  rank: number;
  blockId: string;
  title: string;
  state: string;
  opportunities: string[];
}

export interface DashboardModel {
  customer: string;
  instrumentTitle: string;
  instrumentVersion: string;
  assessedOn: string;
  definitionHash: string;
  templateId: string;
  templateVersion: string;
  derivation: string;
  scale: { min: number; max: number };
  overall: { state: string | null; average: number | null; isPartial: boolean; isComplete: boolean; partialLabel: string };
  coverage: { assessed: number; applicable: number; ratio: number; text: string };
  areaStatus: { complete: number; incomplete: number; notApplicable: number };
  kpis: DashboardKpi[];
  bars: AreaBar[];
  attention: AttentionItem[];
  focus: FocusItem[];
  focusNote: string | null;
  recommendationCount: number;
}

const fmt = (n: number) => n.toFixed(2);
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export function buildDashboardModel(model: PmoReportModel, scale: { min: number; max: number }): DashboardModel {
  const o = model.overall;
  const focusRank = new Map(model.focusAreas.map((f) => [f.blockId, f.rank]));

  const bars: AreaBar[] = model.areas
    .slice()
    .sort((a, b) => a.sequence - b.sequence)
    .map((a) => ({
      blockId: a.blockId,
      sequence: a.sequence,
      title: a.title,
      status: a.status,
      ratio: a.status === "complete" ? shareOfScale(a.average, scale) : null,
      average: a.average,
      state: a.state,
      focusRank: focusRank.get(a.blockId) ?? null,
      assessed: a.assessed,
      applicable: a.applicable,
      notInPlace: a.notInPlace,
      justification: a.justification,
    }));

  const attention: AttentionItem[] = [];
  for (const a of bars) {
    if (a.status === "incomplete") {
      const missing = a.applicable - a.assessed;
      attention.push({
        kind: "incomplete",
        blockId: a.blockId,
        title: a.title,
        text: `${a.assessed} of ${a.applicable} applicable ${plural(a.applicable, "question", "questions")} assessed; ${missing} could not be assessed. The area is not classified.`,
      });
    } else if (a.status === "not_applicable") {
      attention.push({
        kind: "not_applicable",
        blockId: a.blockId,
        title: a.title,
        text: a.justification ? `Not applicable — reason given: ${a.justification}` : "Not applicable — no reason given.",
      });
    }
  }

  const coverageRatio = o.questionsApplicable > 0 ? o.questionsAssessed / o.questionsApplicable : 0;
  const recommendationCount = model.opportunities.length;

  const kpis: DashboardKpi[] = [
    {
      key: "overall",
      label: "Overall",
      value: o.state ?? "Not classified",
      detail:
        o.average === null
          ? "No fully assessed practice area."
          : `average ${fmt(o.average)} on the ${scale.min}–${scale.max} answer scale` + (o.isPartial ? " · Partial" : ""),
      tone: o.state === null ? "muted" : o.isPartial ? "warn" : "neutral",
    },
    {
      key: "coverage",
      label: "Coverage",
      value: `${o.questionsAssessed} of ${o.questionsApplicable}`,
      detail: o.coverageText,
      tone: o.isComplete ? "ok" : "warn",
    },
    {
      key: "areas",
      label: "Practice areas",
      value: `${o.areasComplete} complete`,
      detail: `${o.areasIncomplete} incomplete · ${o.areasNotApplicable} not applicable`,
      tone: o.areasIncomplete > 0 ? "warn" : "neutral",
    },
    {
      key: "recommendations",
      label: "Recommendations",
      value: String(recommendationCount),
      detail:
        model.focusAreas.length > 0
          ? `in ${model.focusAreas.length} suggested focus ${plural(model.focusAreas.length, "area", "areas")}`
          : "no focus area suggested",
      tone: "neutral",
    },
  ];

  return {
    customer: model.identity.customer,
    instrumentTitle: model.identity.instrumentTitle,
    instrumentVersion: model.identity.instrumentVersion,
    assessedOn: model.identity.assessedOn,
    definitionHash: model.identity.definitionHash,
    templateId: model.template.id,
    templateVersion: model.template.version,
    derivation: o.derivation,
    scale,
    overall: {
      state: o.state,
      average: o.average,
      isPartial: o.isPartial,
      isComplete: o.isComplete,
      partialLabel: model.template.partialLabel,
    },
    coverage: { assessed: o.questionsAssessed, applicable: o.questionsApplicable, ratio: coverageRatio, text: o.coverageText },
    areaStatus: { complete: o.areasComplete, incomplete: o.areasIncomplete, notApplicable: o.areasNotApplicable },
    kpis,
    bars,
    attention,
    focus: model.focusAreas.map((f) => ({
      rank: f.rank,
      blockId: f.blockId,
      title: f.title,
      state: f.state,
      opportunities: f.opportunities.map((x) => (x.reason ? `${x.text} — Your reason: ${x.reason}` : x.text)),
    })),
    focusNote: model.focusNote,
    recommendationCount,
  };
}
